import * as Dialog from '@radix-ui/react-dialog';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';

type Size = 'sm' | 'md' | 'lg';
type Side = 'right' | 'left';

const SIZE: Record<Size, string> = {
  sm: 'w-[320px]',
  md: 'w-[480px]',
  lg: 'w-[640px]',
};

export interface DrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  size?: Size;
  side?: Side;
  title?: ReactNode;
  footer?: ReactNode;
  className?: string;
  children: ReactNode;
}

export function Drawer({
  open,
  onOpenChange,
  size = 'md',
  side = 'right',
  title,
  footer,
  className,
  children,
}: DrawerProps) {
  const sideClass = side === 'right' ? 'right-0' : 'left-0';
  const animClass =
    side === 'right'
      ? 'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right'
      : 'data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left';

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          className={clsx(
            'fixed top-0 bottom-0 z-50',
            sideClass,
            SIZE[size],
            'max-w-[85vw] bg-bg-card text-fg-primary shadow-2xl',
            'border-line',
            side === 'right' ? 'border-l' : 'border-r',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            animClass,
            'flex flex-col',
            className,
          )}
        >
          {title && (
            <div className="flex items-center justify-between border-b border-line px-5 py-4">
              <Dialog.Title className="text-base font-semibold tracking-heading">{title}</Dialog.Title>
              <Dialog.Close
                aria-label="Close"
                className="text-fg-muted hover:text-fg-primary text-xl leading-none"
              >
                ×
              </Dialog.Close>
            </div>
          )}
          <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer && (
            <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-3">
              {footer}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
